import { List, Map } from 'immutable';
import { User } from 'records/User';
import { ProjectGroup } from 'records/ProjectGroup';
import { GroupMember } from 'records/GroupMember';

export function alphabetizeGroups(
  projectGroups: List<ProjectGroup>
): List<ProjectGroup> {
  const sortedDropdownList = projectGroups.sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }

    return 1;
  });

  return sortedDropdownList;
}

export function sortGroupsByName(
  projectGroups: List<ProjectGroup>,
  direction: string
): List<ProjectGroup> {
  const sortedGroups = projectGroups.sort((groupA, groupB): number => {
    const a =
      direction === 'ascending'
        ? groupA.name.toLowerCase()
        : groupB.name.toLowerCase();
    const b =
      direction === 'ascending'
        ? groupB.name.toLowerCase()
        : groupA.name.toLowerCase();

    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }

    return 0;
  });

  return sortedGroups;
}

export function getGroupsByMember(
  projectGroups: List<ProjectGroup>
): Map<string, List<ProjectGroup>> {
  const membersInGroups = projectGroups.reduce(
    (
      acc: Map<string, List<ProjectGroup>>,
      group: ProjectGroup
    ): Map<string, List<ProjectGroup>> => {
      if (
        group.members === undefined ||
        (group.members && group.members.length === 0)
      )
        return acc;
      const memberMap = group.members.reduce((memberAcc, member) => {
        const existingGroups = acc.get(member.id) || List<ProjectGroup>();
        const updatedGroups = [...existingGroups, group];
        return memberAcc.setIn([member.id], updatedGroups);
      }, Map<string, List<ProjectGroup>>());

      return acc.merge(memberMap);
    },
    Map<string, List<ProjectGroup>>()
  );

  return membersInGroups;
}

export function getMembersAllInSameGroups(
  preSelectedGroups: List<ProjectGroup>,
  selectedUsers: List<User>
): boolean {
  if (preSelectedGroups.size === 0) return false;

  const selectedUserIds = selectedUsers.map((user) => user.id);
  const membersAreInSameGroups = preSelectedGroups.every(
    (group: ProjectGroup): boolean => {
      if (
        group.members === undefined ||
        (group.members && group.members.length === 0) ||
        group.members.length < selectedUsers.size
      )
        return false;
      if (selectedUsers.size === 1) return true;
      const groupMemberIds = group.members.map((member) => member.id);
      const isInAllGroups = selectedUserIds.every((id) => {
        return groupMemberIds.includes(id);
      });
      return isInAllGroups;
    },
    List()
  );

  return membersAreInSameGroups;
}

export function getGroupHasAllSelectedMembers(
  group: ProjectGroup,
  selectedUsers: List<User>
): boolean {
  if (
    group.members === undefined ||
    (group.members && group.members.length === 0) ||
    group.members.length < selectedUsers.size
  )
    return false;

  const selectedMemberIds = group.members.map((member) => member.id);
  const groupsHasAllMembers = selectedUsers.every((user) => {
    return selectedMemberIds.includes(user.id);
  });

  return groupsHasAllMembers;
}

export function getLowercaseGroupNames(
  projectGroups: List<ProjectGroup>
): List<string> {
  return projectGroups.map((group) => group.name.toLowerCase());
}

export function getUpdatedSelectedTags(
  selectedGroups: List<ProjectGroup>,
  allGroups: List<ProjectGroup>
): List<ProjectGroup> {
  const updatedTags: List<ProjectGroup> = selectedGroups.reduce(
    (acc, selectedGroup) => {
      const tagToUpdate = allGroups.find(
        (group) =>
          group.groupid === selectedGroup.groupid &&
          group.name !== selectedGroup.name
      );

      let updatedList: List<ProjectGroup> = List();
      if (tagToUpdate && !acc.includes(tagToUpdate)) {
        updatedList = acc.push(tagToUpdate);
      } else if (!acc.includes(selectedGroup)) {
        updatedList = acc.push(selectedGroup);
      }

      return updatedList;
    },
    List()
  );
  return updatedTags;
}

export function getGroupNamesFromIds(
  allGroups: List<ProjectGroup>,
  groupids: string[]
): string[] {
  const groupNames = groupids.reduce(
    (acc: string[], groupid: string): string[] => {
      const groupRecord = allGroups.find((group) => {
        return group.groupid === groupid;
      });

      if (!groupRecord) return acc;

      const groupName = groupRecord.name;
      const updatedGroupNames: string[] = [...acc, groupName];
      return updatedGroupNames;
    },
    []
  );

  return groupNames;
}

export function getMembersFromGroupIds(
  allGroups: List<ProjectGroup>,
  groupids: string[]
): GroupMember[] {
  const groupMembers = groupids.reduce(
    (acc: GroupMember[], groupid: string): GroupMember[] => {
      const groupRecord = allGroups.find((group) => {
        return group.groupid === groupid;
      });

      if (!groupRecord || groupRecord.members.length === 0) return acc;

      const updatedGroupMembers: GroupMember[] = [
        ...acc,
        ...groupRecord.members,
      ];
      return updatedGroupMembers;
    },
    []
  );

  return groupMembers;
}

export function getMembersByGroupIds(
  allGroups: List<ProjectGroup>,
  groupids: string[]
): Map<string, GroupMember[]> {
  const groupMembers = groupids.reduce(
    (
      acc: Map<string, GroupMember[]>,
      groupid: string
    ): Map<string, GroupMember[]> => {
      const groupRecord = allGroups.find((group) => {
        return group.groupid === groupid;
      });

      if (!groupRecord || groupRecord.members.length === 0) return acc;

      const groupMap = groupRecord.members.reduce((memberAcc, member) => {
        const existingGroupMembers = memberAcc.get(groupid) || [];
        const updatedGroupMembers = [...existingGroupMembers, member];
        return memberAcc.setIn([groupid], updatedGroupMembers);
      }, Map<string, GroupMember[]>());

      return acc.merge(groupMap);
    },
    Map<string, GroupMember[]>()
  );

  return groupMembers;
}
